/**
 * Visibility gate for the bottom-right "Instant Access" launcher pill (the FAB).
 *
 * The pill is hidden for ordinary storefront traffic. It renders only for a
 * session that arrived through one of the panel entry-point links we already
 * send out — the account-page / Klaviyo link (`?so_panel_tab`) or the email
 * magic-link (`?so-token`) — so a customer who closes the panel still has a way
 * back in, while shoppers who never followed a link never see it.
 *
 * Deliberately reads the raw URL rather than `getResolvedDefaultPanelTab()`:
 * that helper also honours the `data-so-panel-tab` script attribute and
 * `window.__SOCIAL_OCEAN_BUY_AGAIN__.defaultPanelTab`, either of which a theme
 * could set for every visitor — which would un-hide the pill store-wide and
 * defeat the point of the gate.
 *
 * The sessionStorage latch is required, not a convenience: `?so-token` is
 * stripped from the URL by `removeTokenFromUrl()` as soon as it is consumed,
 * and navigating to any other store page drops the query string entirely.
 *
 * Mirrors the `?so_cancel_chat` override in
 * `components/manage-subscription/cancel-chat-override.ts`.
 */

const FAB_SESSION_KEY = "so_fab_visible";

/** True when the URL carries one of the panel entry-point params. */
export function hasFabEntryParam(search: string): boolean {
  const params = new URLSearchParams(search);
  const panelTab = params.get("so_panel_tab");
  const token = params.get("so-token");
  return (
    (panelTab != null && panelTab !== "") || (token != null && token !== "")
  );
}

/** Latches on arrival, then sticks for the rest of the tab session. */
export function isFabVisible(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (hasFabEntryParam(window.location.search)) {
      sessionStorage.setItem(FAB_SESSION_KEY, "1");
      return true;
    }
    return sessionStorage.getItem(FAB_SESSION_KEY) === "1";
  } catch {
    // sessionStorage unavailable (private mode) — fall back to the URL alone.
    return hasFabEntryParam(window.location.search);
  }
}
